/* San Rocco Italia: native theme shipping bar v9. No cookies or external services. */
(function () {
  'use strict';
  const selector = '[data-sri-shipping]';
  const dismissalKey = 'sri-shipping-announcement-dismissed';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let cartTimer;
  let refreshSerial = 0;
  const countryCurrency = { US:'en-US',GB:'en-GB',AU:'en-AU',CA:'en-CA',NZ:'en-NZ',CH:'de-CH' };
  let infoSerial = 0;
  let activeInfo = null;

  // Region-specific checkout exceptions. These remain available in the info
  // tooltip even when a threshold rule is simply US|USD|300 or PT|EUR|50.
  function regionalInfo(node, rule) {
    const country = (node.dataset.country || '').toUpperCase();
    const currency = (node.dataset.currency || '').toUpperCase();
    if (country === 'US' && currency === 'USD') {
      return 'Free shipping applies to eligible US destinations on qualifying orders of $300 or more. American Samoa, the Marshall Islands, the Northern Mariana Islands, Palau and the US Virgin Islands are excluded from this offer. Puerto Rico has separate shipping rates.';
    }
    if (country === 'PT' && currency === 'EUR') {
      return 'Free shipping from €50 applies to mainland Portugal only. Madeira and the Azores are not included.';
    }
    if (country === 'GB' && currency === 'GBP') {
      return 'Free shipping from £50 applies to the eligible UK regions. Northern Ireland is in a separate shipping zone; rates are confirmed at checkout.';
    }
    // Other destinations may carry an optional fourth | field in theme settings.
    return rule.note || '';
  }


  function parseRule(node) {
    const country = (node.dataset.country || '').toUpperCase();
    const currency = (node.dataset.currency || '').toUpperCase();
    const rows = (node.dataset.rules || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    const rules = rows.map(row => {
      const columns = row.split('|').map(s => (s || '').trim());
      const key = String(columns[0] || '').toUpperCase();
      const iso = String(columns[1] || '').toUpperCase();
      const parsed = Number(columns[2]);
      const note = columns.slice(3).join('|').trim();
      return { key, iso, amount: parsed, note };
    }).filter(r => /^[A-Z]{2}$/.test(r.key) && /^[A-Z]{3}$/.test(r.iso) && Number.isFinite(r.amount) && r.amount >= 0);
    // Exact destination AND presentment currency are mandatory.
    // Generic EUR matching can otherwise mislead Malta, Switzerland or other zones.
    return rules.find(r => r.key === country && r.iso === currency) || null;
  }

  function formatMoney(minorUnits, currency, country) {
    const locale = countryCurrency[country] || document.documentElement.lang || 'en-GB';
    try { return new Intl.NumberFormat(locale, { style:'currency', currency, maximumFractionDigits:2 }).format(minorUnits / 100); }
    catch (_) { return `${currency} ${(minorUnits/100).toFixed(2)}`; }
  }

  function fill(template, values) {
    return String(template || '').replace(/\{(goal|remaining)\}/g, (_, key) => values[key] || '');
  }

  function eligibleVendors(node) {
    return new Set((node.dataset.eligibleVendors || '')
      .split(/[\r\n,]+/)
      .map(v => v.trim().toLocaleLowerCase())
      .filter(Boolean));
  }

  // In restricted mode, count only the discounted value of matching vendors.
  // A cart-level discount is apportioned to qualifying lines for a best-effort
  // estimate. Actual shipping rates are always determined by Shopify checkout.
  function qualifyingCart(node, cart) {
    const restricted = node.dataset.vendorMode === 'listed';
    if (!restricted) {
      const total = Number(cart && cart.total_price != null ? cart.total_price : node.dataset.initialTotal || 0);
      return {total, restricted:false, hasExcluded:false, hasQualifying:total > 0, hasItems:!!(cart && cart.item_count)};
    }
    const vendors = eligibleVendors(node);
    if (!vendors.size || !cart || !Array.isArray(cart.items)) return null;
    let qualifying = 0;
    let allLines = 0;
    let hasExcluded = false;
    let hasQualifying = false;
    for (const item of cart.items) {
      const amount = Math.max(0, Number(item.final_line_price ?? item.line_price ?? 0));
      allLines += amount;
      if (vendors.has(String(item.vendor || '').trim().toLocaleLowerCase())) {
        qualifying += amount;
        hasQualifying = true;
      } else {
        hasExcluded = true;
      }
    }
    // Account approximately for order-wide cart discounts not already included
    // in the per-item prices. Avoid increasing the eligible amount if a checkout
    // surcharge or an unusual line-item total is present.
    const cartTotal = Number(cart.total_price);
    if (allLines > 0 && Number.isFinite(cartTotal) && cartTotal < allLines) {
      qualifying = Math.max(0, Math.round(qualifying * Math.max(0, cartTotal) / allLines));
    }
    return {total:qualifying, restricted:true, hasExcluded, hasQualifying,
      hasItems:cart.items.length > 0};
  }

  function update(node, cart) {
    // Never reveal a visitor-dismissed bar when cart events refresh the message.
    if (node.dataset.sriDismissed === 'true') { closeInfo(node.querySelector('[data-sri-info]')); node.hidden = true; return; }
    const rule = parseRule(node);
    if (!rule) { closeInfo(node.querySelector('[data-sri-info]')); node.hidden = true; return; }
    const qualifying = qualifyingCart(node, cart);
    // For the restricted filter, never show an unfiltered estimate while waiting
    // for /cart.js. Also hide misconfigured filters that have no eligible vendors.
    if (!qualifying) { closeInfo(node.querySelector('[data-sri-info]')); node.hidden = true; return; }
    node.hidden = false;
    const currency = (node.dataset.currency || '').toUpperCase();
    const country = (node.dataset.country || '').toUpperCase();
    const threshold = Math.round(rule.amount * 100);
    const total = Math.max(0,qualifying.total);
    const remaining = Math.max(0, threshold - total);
    const reached = threshold === 0 || (qualifying.hasQualifying && total >= threshold);
    const progress = threshold === 0 ? 100 : Math.max(0, Math.min(100, total / threshold * 100));
    node.style.setProperty('--sri-percent', progress + '%');
    const values = { goal:formatMoney(threshold,currency,country), remaining:formatMoney(remaining,currency,country) };
    const message = node.querySelector('[data-sri-message]');
    let template;
    if (qualifying.restricted && qualifying.hasItems && !qualifying.hasQualifying) {
      template = node.dataset.vendorNoEligible;
    } else if (threshold === 0) {
      template = qualifying.restricted ? node.dataset.vendorEmpty : node.dataset.free;
    } else if (reached) {
      template = qualifying.restricted && qualifying.hasExcluded ? node.dataset.vendorReachedMixed : node.dataset.reached;
    } else if (!qualifying.hasItems) {
      template = qualifying.restricted ? node.dataset.vendorEmpty : node.dataset.empty;
    } else if (qualifying.restricted && qualifying.hasExcluded) {
      template = node.dataset.vendorRemainingMixed;
    } else {
      template = node.dataset.remaining;
    }
    if (message) message.textContent = fill(template, values);
    // Make exceptions available via an accessible icon, without lengthening
    // the bar's promotional message or losing the warnings at goal reached.
    const info = node.querySelector('[data-sri-info]');
    if (info) {
      const detail = regionalInfo(node, rule);
      info.hidden = !detail;
      const panel = info._sriPanel || info.querySelector('[data-sri-info-panel]');
      if (panel) panel.textContent = detail;
      if (!detail) closeInfo(info);
    }
    const track = node.querySelector('[data-sri-track]');
    if (track) {
      track.hidden = threshold === 0;
      const percentage = Math.max(0, Math.min(100, Math.round(progress)));
      const bar = node.querySelector('[data-sri-progress]');
      if (bar) bar.style.width = percentage + '%';
      track.setAttribute('aria-valuenow',String(percentage));
    }
    const upsell = node.querySelector('[data-sri-upsell]');
    if (upsell) {
      const variantId = Number(upsell.dataset.variantId);
      const alreadyInCart = !!(cart && Array.isArray(cart.items) && cart.items.some(item => Number(item.variant_id) === variantId));
      const allowReached = node.dataset.showUpsellOnReached === 'true';
      upsell.hidden = alreadyInCart || (reached && !allowReached);
    }
  }

  // Build the information control beside the progress message in JS so
  // existing Shopify section files and customiser settings need no changes.
  function enhanceInfoButtons() {
    document.querySelectorAll(selector).forEach(node => {
      const message = node.querySelector('[data-sri-message]');
      if (!message || node.querySelector('[data-sri-info]')) return;
      const row = document.createElement('div');
      row.className = 'sri-shipping__message-row';
      message.parentNode.insertBefore(row, message);
      row.appendChild(message);
      const info = document.createElement('span');
      info.className = 'sri-shipping__info';
      info.setAttribute('data-sri-info', '');
      info.hidden = true;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sri-shipping__info-button';
      button.setAttribute('data-sri-info-button', '');
      button.setAttribute('aria-label', 'Free shipping eligibility and regional exclusions');
      button.setAttribute('aria-expanded', 'false');
      button.textContent = 'i';
      const panel = document.createElement('span');
      panel.className = 'sri-shipping__info-panel';
      panel.setAttribute('data-sri-info-panel', '');
      panel.id = 'sri-shipping-info-' + (++infoSerial);
      panel.setAttribute('role', 'region');
      panel.setAttribute('aria-label', 'Shipping eligibility details');
      panel.hidden = true;
      button.setAttribute('aria-controls', panel.id);
      info.appendChild(button);
      info.appendChild(panel);
      info._sriPanel = panel;
      row.appendChild(info);
      // When open, the popover is moved to document.body to escape
      // the header's overflow and stacking contexts. Hovering over the
      // detached panel should not prematurely close it.
      panel.addEventListener('pointerenter', () => {
        window.clearTimeout(info._sriCloseTimer);
      });
      panel.addEventListener('pointerleave', () => {
        if (info.dataset.sriPinned !== 'true') {
          info._sriCloseTimer = window.setTimeout(() => closeInfo(info), 180);
        }
      });
      info.addEventListener('pointerenter', () => {
        window.clearTimeout(info._sriCloseTimer);
        openInfo(info, false);
      });
      info.addEventListener('pointerleave', () => {
        if (info.dataset.sriPinned !== 'true') {
          info._sriCloseTimer = window.setTimeout(() => closeInfo(info), 230);
        }
      });
    });
  }

  // A high z-index inside the bar cannot rise above a different Shopify
  // announcement/header stacking context. Render the opened panel under
  // <body>, where it can overlay every header strip without changing layout.
  function positionInfo(info) {
    if (!info || info !== activeInfo) return;
    const button = info.querySelector('[data-sri-info-button]');
    const panel = info._sriPanel;
    if (!button || !panel || panel.hidden) return;
    const anchor = button.getBoundingClientRect();
    const viewportW = document.documentElement.clientWidth;
    const viewportH = window.innerHeight;
    if (anchor.bottom < 0 || anchor.top > viewportH || !button.isConnected) {
      closeInfo(info);
      return;
    }
    // Use a fixed position relative to the visible viewport, adjusting for
    // narrow screens and flipping upwards if there is no space below.
    const panelWidth = panel.offsetWidth;
    const panelHeight = panel.offsetHeight;
    const left = Math.max(10, Math.min(viewportW - panelWidth - 10,
      anchor.left + anchor.width / 2 - panelWidth / 2));
    const bottomSpace = viewportH - anchor.bottom;
    const top = bottomSpace >= panelHeight + 12 || anchor.top < panelHeight + 12
      ? Math.min(viewportH - panelHeight - 10, anchor.bottom + 8)
      : Math.max(10, anchor.top - panelHeight - 8);
    panel.style.left = Math.round(left) + 'px';
    panel.style.top = Math.round(top) + 'px';
  }

  function openInfo(info, pinned) {
    if (!info || info.hidden) return;
    window.clearTimeout(info._sriCloseTimer);
    const panel = info._sriPanel;
    const button = info.querySelector('[data-sri-info-button]');
    if (!panel || !button) return;
    if (activeInfo && activeInfo !== info) closeInfo(activeInfo);
    if (pinned) info.dataset.sriPinned = 'true';
    // Moving the actual node preserves its ARIA id, events and current text.
    if (panel.parentNode !== document.body) document.body.appendChild(panel);
    panel.hidden = false;
    activeInfo = info;
    button.setAttribute('aria-expanded', 'true');
    positionInfo(info);
  }

  function closeInfo(info) {
    if (!info) return;
    window.clearTimeout(info._sriCloseTimer);
    info.dataset.sriPinned = 'false';
    const panel = info._sriPanel || info.querySelector('[data-sri-info-panel]');
    const button = info.querySelector('[data-sri-info-button]');
    if (panel) {
      panel.hidden = true;
      panel.style.left = '';
      panel.style.top = '';
      if (panel.parentNode !== info) info.appendChild(panel);
    }
    if (button) button.setAttribute('aria-expanded', 'false');
    if (activeInfo === info) activeInfo = null;
  }

  function onInfoClick(ev) {
    const button = ev.target.closest('[data-sri-info-button]');
    if (button) {
      const info = button.closest('[data-sri-info]');
      if (info.dataset.sriPinned === 'true') closeInfo(info);
      else openInfo(info, true);
      return;
    }
    if (!ev.target.closest('[data-sri-info], [data-sri-info-panel]')) {
      document.querySelectorAll('[data-sri-info]').forEach(closeInfo);
    }
  }

  function onInfoEscape(ev) {
    if (ev.key !== 'Escape') return;
    const open = Array.from(document.querySelectorAll('[data-sri-info]'))
      .find(info => info.querySelector('[data-sri-info-button][aria-expanded="true"]'));
    if (open) {
      closeInfo(open);
      open.querySelector('[data-sri-info-button]').focus();
    }
  }

  // Small accessible close button for the top announcement bar only.
  // Session storage remembers the visitor's preference across page navigation,
  // but allows the bar to return in a new browsing session.
  function enhanceDismissButtons() {
    document.querySelectorAll(selector + '[data-mode="announcement"]').forEach(node => {
      if (!node.querySelector('[data-sri-dismiss]')) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sri-shipping__dismiss';
        button.setAttribute('data-sri-dismiss', '');
        button.setAttribute('aria-label', 'Close free shipping announcement');
        button.title = 'Close shipping bar';
        button.textContent = '×';
        node.appendChild(button);
      }
      try {
        if (window.sessionStorage.getItem(dismissalKey) === '1') {
          node.dataset.sriDismissed = 'true';
          node.hidden = true;
        }
      } catch (_) { /* Still dismissible when storage is blocked */ }
    });
  }

  function onDismiss(ev) {
    const button = ev.target.closest('[data-sri-dismiss]');
    if (!button) return;
    const node = button.closest(selector);
    if (!node) return;
    closeInfo(node.querySelector('[data-sri-info]'));
    node.dataset.sriDismissed = 'true';
    node.hidden = true;
    try { window.sessionStorage.setItem(dismissalKey, '1'); }
    catch (_) { /* Dismiss this page only */ }
  }

  function updateAll(cart) {
    document.querySelectorAll(selector).forEach(node => update(node,cart));
  }

  async function fetchCart() {
    const serial = ++refreshSerial;
    try {
      const resp = await fetch(root + 'cart.js', { credentials:'same-origin', cache:'no-store' });
      if (!resp.ok) return;
      const cart = await resp.json();
      if (serial === refreshSerial) updateAll(cart);
    } catch (_) { /* Initial server-rendered amount remains as fallback */ }
  }

  function refresh(afterMs) {
    window.clearTimeout(cartTimer);
    cartTimer = window.setTimeout(fetchCart,afterMs || 0);
  }

  function onAdd(ev) {
    const button = ev.target.closest('[data-sri-add]');
    if (!button) return;
    ev.preventDefault();
    if (button.disabled) return;
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;
    button.disabled = true;
    const upsell = button.closest('[data-sri-upsell]');
    const error = upsell && upsell.querySelector('[data-sri-error]');
    if (error) error.hidden = true;
    fetch(root + 'cart/add.js', {
      method:'POST', credentials:'same-origin',
      headers:{ 'Content-Type':'application/json', 'Accept':'application/json' },
      body:JSON.stringify({items:[{id:variantId, quantity:1}]})
    }).then(async r => {
      const response = await r.json();
      if (!r.ok) throw new Error(response.description || 'Unable to add this product.');
      document.dispatchEvent(new CustomEvent('sri:cart:updated'));
      if (window.location.pathname.replace(/\/$/,'').endsWith('/cart')) window.location.reload();
      else refresh(200);
    }).catch(e => {
      if (error) { error.textContent = e.message; error.hidden = false; }
    }).finally(() => { button.disabled = false; });
  }

  function init() {
    if (!document.querySelector(selector)) return;
    enhanceDismissButtons();
    enhanceInfoButtons();
    updateAll();
    refresh(0);
    if (window.__sriShippingLoaded) return;
    window.__sriShippingLoaded = true;
    document.addEventListener('click', onAdd);
    document.addEventListener('click', onDismiss);
    document.addEventListener('click', onInfoClick);
    document.addEventListener('keydown', onInfoEscape);
    // Keep the viewport-level popover anchored during scrolling and resizing.
    window.addEventListener('resize', () => positionInfo(activeInfo));
    window.addEventListener('scroll', () => positionInfo(activeInfo), true);
    document.addEventListener('shopify:section:unload', () => closeInfo(activeInfo));
    document.addEventListener('sri:cart:updated', () => refresh(150));
    document.addEventListener('cart:updated', () => refresh(150));
    document.addEventListener('cart:refresh', () => refresh(150));
    document.addEventListener('change', e => {
      if (e.target.matches('input[name="updates[]"], input.quantity__input, .quantity input, select[name="quantity"]')) {
        refresh(700); window.setTimeout(fetchCart,1450);
      }
    });
    document.addEventListener('click', e => {
      if (e.target.closest('button[name="add"], [name="add"], .quantity__button, cart-remove-button, [data-cart-remove], .cart-remove-button')) {
        refresh(700); window.setTimeout(fetchCart,1450);
      }
    },true);
    window.addEventListener('focus', () => refresh(100));
    document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(100); });
    document.addEventListener('shopify:section:load', () => {
      enhanceDismissButtons(); enhanceInfoButtons(); updateAll(); refresh(100);
    });
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => refresh(100));
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',init);
  else init();
})();
